import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { isCustomerBrowseEnabled } from '../../config/customerFeatureFlags.js';
import {
  PLATFORM_BROWSE_PATH,
  PLATFORM_ORGS_PATH,
  resolveBootCustomerFeatureFlags,
  resolveGuestShopHomeHref,
  resolvePlatformBrowseParentPath,
  resolvePlatformHubPath,
} from '../shop/resolveShopHref.js';

export const AUTH_INTENT_QUERY_KEY = 'authIntent';
export const AUTH_INTENT_SIGN_UP = 'sign-up';

/**
 * SSOT (product freeze): login identity is never `?tenant=`.
 * - Visit / browse / register: tenant from URL **path** (or register-only visit query).
 * - Login chrome: bare `/sign-in`; from a tenant context: `/{tenant}/sign-in`.
 * Register helpers below may still append `?tenant=` as visit-tenant (not login bind).
 */
/** Append `?tenant=` when org is a real tenant (never for platform chrome). Register/visit only. */
function withOptionalTenantQuery(path: string, tenantCode: string): string {
  if (isPlatformChromeTenant(tenantCode)) {
    return path;
  }
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}tenant=${encodeURIComponent(tenantCode)}`;
}

/**
 * Wave C / G13 — inject `tenant=<pathSlug>` into RR `location.search` when missing.
 * Preserves other params; does not duplicate when `tenant=` is already present.
 * Skips blank / platform-chrome path slugs (never emit `?tenant=__platform__`).
 */
export function ensureTenantInSearch(search: string, pathSlug: string): string {
  const slug = pathSlug.trim();
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  if (
    slug.length === 0 ||
    isPlatformChromeTenant(slug) ||
    params.has('tenant')
  ) {
    if (search.length === 0 || search === '?') {
      return '';
    }
    return search.startsWith('?') ? search : `?${raw}`;
  }
  params.set('tenant', slug);
  const nextQuery = params.toString();
  return nextQuery.length > 0 ? `?${nextQuery}` : '';
}

/**
 * Login href SSOT — path-only (never `/sign-in?tenant=` as login org bind).
 * Platform chrome → bare `/sign-in`; real org → `/{tenant}/sign-in`.
 */
export function resolveCustomerSignInPath(tenantCode: string): string {
  if (isPlatformChromeTenant(tenantCode)) {
    return '/sign-in';
  }
  return `/${encodeURIComponent(tenantCode)}/sign-in`;
}

/** Platform chrome registration form (`/sign-up` with org select). */
export const PLATFORM_SIGN_UP_PATH = '/sign-up';

/** Real-tenant sign-up, or platform `/sign-up` registration form under chrome. */
export function resolveCustomerSignUpPath(tenantCode: string): string {
  return withOptionalTenantQuery(PLATFORM_SIGN_UP_PATH, tenantCode);
}

/**
 * Sign-up with optional `?oidc=` / `&oidc=` so Register can auto-start Google/Apple
 * after the user ticks legal consents (sign-in never collects ticks).
 */
export function resolveCustomerSignUpPathWithOidc(
  tenantCode: string,
  provider?: 'google' | 'apple' | null,
): string {
  const base = resolveCustomerSignUpPath(tenantCode);
  if (provider !== 'google' && provider !== 'apple') {
    return base;
  }
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}oidc=${encodeURIComponent(provider)}`;
}

/** Platform `/sign-up/pending`, optional `?tenant=` when org is known. */
export function resolveCustomerSignUpPendingPath(tenantCode: string): string {
  return withOptionalTenantQuery('/sign-up/pending', tenantCode);
}

/** Platform chrome `/onboarding/set-credentials` only — never `/{tenant}/onboarding/...`. */
export function resolveCustomerSetCredentialsPath(tenantCode: string): string {
  return withOptionalTenantQuery('/onboarding/set-credentials', tenantCode);
}

/** Platform `/forgot-password`, optional `?tenant=`. */
export function resolveCustomerForgotPasswordPath(tenantCode: string): string {
  return withOptionalTenantQuery('/forgot-password', tenantCode);
}

/** Platform `/reset-password`, optional `?tenant=`. */
export function resolveCustomerResetPasswordPath(tenantCode: string): string {
  return withOptionalTenantQuery('/reset-password', tenantCode);
}

/** True when the org picker should drive registration (legacy `?authIntent=sign-up` on `/` or `/orgs`). */
export function isPlatformSignUpOrgPicker(pathname: string, search: string): boolean {
  const onOrgs =
    pathname === PLATFORM_ORGS_PATH ||
    pathname.startsWith(`${PLATFORM_ORGS_PATH}/`) ||
    pathname === '/';
  return onOrgs && readAuthIntentSignUp(search);
}

/** Real-tenant shops directory, or platform recovery home (Nakupovat / Oblíbené). */
export function resolveCustomerShopFallbackPath(tenantCode: string): string {
  if (isPlatformChromeTenant(tenantCode)) {
    return resolveGuestShopHomeHref();
  }
  return `/${encodeURIComponent(tenantCode)}/shops`;
}

/**
 * `/__platform__/*` is never a valid tenant URL — map to platform chrome routes.
 */
export function resolveIllegalPlatformTenantRedirect(pathname: string): string {
  const withoutPrefix = pathname.replace(/^\/__platform__(?=\/|$)/, '') || '/';
  if (withoutPrefix === '/sign-up' || withoutPrefix.startsWith('/sign-up/')) {
    return PLATFORM_SIGN_UP_PATH;
  }
  if (withoutPrefix === '/orgs' || withoutPrefix.startsWith('/orgs/')) {
    return PLATFORM_ORGS_PATH;
  }
  if (withoutPrefix === '/sign-in' || withoutPrefix.startsWith('/sign-in/')) {
    return '/sign-in';
  }
  if (
    withoutPrefix === '/forgot-password' ||
    withoutPrefix.startsWith('/forgot-password/')
  ) {
    return '/forgot-password';
  }
  if (
    withoutPrefix === '/reset-password' ||
    withoutPrefix.startsWith('/reset-password/')
  ) {
    return '/reset-password';
  }
  if (withoutPrefix.startsWith('/confirm-email')) {
    return '/sign-in';
  }
  if (
    withoutPrefix === '/onboarding/set-credentials' ||
    withoutPrefix.startsWith('/onboarding/set-credentials/') ||
    withoutPrefix.startsWith('/onboarding')
  ) {
    return '/onboarding/set-credentials';
  }
  if (withoutPrefix === '/account' || withoutPrefix.startsWith('/account/')) {
    return withoutPrefix;
  }
  if (withoutPrefix === '/browse' || withoutPrefix.startsWith('/browse/')) {
    return resolvePlatformBrowseParentPath();
  }
  if (withoutPrefix === '/scan' || withoutPrefix.startsWith('/scan/')) {
    return withoutPrefix;
  }
  if (
    withoutPrefix === '/receipt-recovery' ||
    withoutPrefix.startsWith('/receipt-recovery/')
  ) {
    return withoutPrefix;
  }
  return '/';
}

/**
 * `/login` and `/login/*` are common aliases for tenant-less auth — never a real
 * tenant slug. Map to platform `/sign-in` / `/sign-up` so ApiClient does not call
 * `/api/login/v1/...`.
 */
export function resolveLegacyLoginPathRedirect(pathname: string): string | null {
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  if (trimmed === '/login') {
    return '/sign-in';
  }
  if (trimmed === '/login/sign-in' || trimmed.startsWith('/login/sign-in/')) {
    return '/sign-in';
  }
  if (trimmed === '/login/sign-up' || trimmed.startsWith('/login/sign-up/')) {
    return PLATFORM_SIGN_UP_PATH;
  }
  if (
    trimmed === '/login/forgot-password' ||
    trimmed.startsWith('/login/forgot-password/')
  ) {
    return '/forgot-password';
  }
  if (
    trimmed === '/login/reset-password' ||
    trimmed.startsWith('/login/reset-password/')
  ) {
    return '/reset-password';
  }
  if (trimmed.startsWith('/login/')) {
    return '/sign-in';
  }
  return null;
}

/** URL segments that must never be treated as `/:tenantCode`. */
export const RESERVED_CUSTOMER_PATH_SEGMENTS = new Set([
  'login',
  'sign-in',
  'sign-up',
  'forgot-password',
  'reset-password',
  'confirm-email',
  'onboarding',
  'orgs',
  'browse',
  'shops',
  'favorites',
  'account',
  'scan',
  'receipt-recovery',
  // Wave D platform overflow hubs
  'more',
  'help',
  'orders',
]);

/** True when `code` is a reserved chrome path segment (never an API tenant slug). */
export function isReservedCustomerPathSegment(code: string): boolean {
  return RESERVED_CUSTOMER_PATH_SEGMENTS.has(code.trim().toLowerCase());
}

export function resolveReservedTenantSegmentRedirect(
  tenantCode: string,
  pathname: string,
): string | null {
  const segment = tenantCode.trim().toLowerCase();
  if (!RESERVED_CUSTOMER_PATH_SEGMENTS.has(segment)) {
    return null;
  }
  if (segment === 'login') {
    return resolveLegacyLoginPathRedirect(pathname) ?? '/sign-in';
  }
  if (segment === 'onboarding') {
    return '/onboarding/set-credentials';
  }
  if (segment === 'confirm-email') {
    return '/confirm-email';
  }
  if (segment === 'browse') {
    const flags = resolveBootCustomerFeatureFlags();
    if (!isCustomerBrowseEnabled(flags)) {
      return resolvePlatformHubPath(flags);
    }
    if (pathname === '/browse' || pathname.startsWith('/browse/')) {
      return pathname;
    }
    return PLATFORM_BROWSE_PATH;
  }
  if (segment === 'shops') {
    return '/shops';
  }
  if (segment === 'favorites') {
    return '/favorites';
  }
  if (segment === 'scan') {
    if (pathname === '/scan' || pathname.startsWith('/scan/')) {
      return pathname;
    }
    return '/scan';
  }
  if (segment === 'receipt-recovery') {
    if (
      pathname === '/receipt-recovery' ||
      pathname.startsWith('/receipt-recovery/')
    ) {
      return pathname;
    }
    return '/receipt-recovery';
  }
  if (segment === 'orgs') {
    return PLATFORM_ORGS_PATH;
  }
  if (segment === 'account') {
    if (pathname === '/account' || pathname.startsWith('/account/')) {
      return pathname;
    }
    return '/account';
  }
  if (segment === 'more') {
    if (pathname === '/more' || pathname.startsWith('/more/')) {
      return pathname;
    }
    return '/more';
  }
  if (segment === 'help') {
    if (pathname === '/help' || pathname.startsWith('/help/')) {
      return pathname;
    }
    return '/help';
  }
  if (segment === 'orders') {
    if (pathname === '/orders/find' || pathname.startsWith('/orders/find/')) {
      return pathname;
    }
    return '/orders/find';
  }
  // Pass full pathname so `/sign-in|sign-up|forgot-password|reset-password/…`
  // map to platform chrome via resolveIllegalPlatformTenantRedirect (same as
  // `/__platform__/sign-in` → `/sign-in`).
  return resolveIllegalPlatformTenantRedirect(pathname);
}

export function readAuthIntentSignUp(search: string): boolean {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  return params.get(AUTH_INTENT_QUERY_KEY) === AUTH_INTENT_SIGN_UP;
}
